import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PartnerService from 'tio-employee/services/partner';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type LpnPage from 'tio-common/models/lpn-page';

type Params = {
  page?: number;
  limit?: number;
  search?: string;
  savings?: string | string[];
  format?: string | string[];
  programs?: string | string[];
  areasOfStudy?: string | string[];
};

/**
 * Savings filter keys from SAVINGS_OPTIONS. Exhaustive union ensures compile
 * errors if keys are added/removed without updating the post-filter logic.
 */
type SavingsTag = 'capped_tuition' | 'family_eligible' | 'no_fees';

/**
 * Ember serializes array query params as comma-separated strings in the URL.
 * This helper converts them back to proper arrays for the store query.
 */
function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

type Meta = {
  page: {
    total: number;
  };
};

const DEFAULT_LIMIT = 50;

export default class AuthenticatedLpnIndexRoute extends Route {
  @service declare partner: PartnerService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  queryParams = {
    page: { refreshModel: true },
    limit: { refreshModel: true },
    search: { refreshModel: true },
    savings: { refreshModel: true },
    format: { refreshModel: true },
    programs: { refreshModel: true },
    areasOfStudy: { refreshModel: true },
  };

  beforeModel() {
    const companySettings = this.partner.companySettings;

    if (!companySettings.lpn?.isEnabled) {
      this.router.transitionTo('authenticated.dashboard');
    }
  }

  async model(params: Params) {
    const companySettings = this.partner.companySettings;
    const page = params.page || 1;
    const limit = params.limit || DEFAULT_LIMIT;
    let meta: Meta = { page: { total: 0 } };

    const savings = toArray(params.savings);
    const format = toArray(params.format);
    const programs = toArray(params.programs);
    const areasOfStudy = toArray(params.areasOfStudy);

    // Shared filter params applied to both queries.
    const baseFilter = {
      ...(params.search ? { search: params.search } : {}),
      ...(savings.length ? { savings: [companySettings.id, ...savings].join(',') } : {}),
      ...(format.length ? { format: [companySettings.id, ...format].join(',') } : {}),
      ...(programs.length ? { programs: [companySettings.id, ...programs].join(',') } : {}),
      ...(areasOfStudy.length
        ? { 'areas-of-study': [companySettings.id, ...areasOfStudy].join(',') }
        : {}),
    };

    // Start both queries before awaiting either so they run in parallel.
    // 1. ALL employer-recommended pages for this company — unbounded by the
    //    current page offset so that recommended schools alphabetically past
    //    the first page are never missed.
    // 2. The current paginated slice of results for the "all partners" list.
    const recommendedQuery = this.store.query('lpn-page', {
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
      // Only the company-setting ID is needed (for matching). Request a single
      // tiny attribute to strip the 40+ fields (large JSONB blobs) that bloat
      // the response from ~6 MB down to a fraction.
      fields: { 'company-settings': 'is-global' },
      filter: {
        ...baseFilter,
        'employer-recommended': companySettings.id as string,
      },
      sort: 'school-name',
      page: { limit: 400 }, // fetch up to the API maximum so we always get all
    });
    const paginatedQuery = this.store
      .query('lpn-page', {
        include: 'company-lpn-settings,company-lpn-settings.company-setting',
        fields: { 'company-settings': 'is-global' },
        page: {
          offset: (page - 1) * limit,
          limit,
        },
        sort: 'school-name',
        filter: baseFilter,
      })
      .then((result) => {
        meta = (result.meta as Meta) || { page: { total: 0 } };
        return result;
      });

    const recommendedRaw = (await recommendedQuery) as LpnPage[];
    const paginatedRaw = (await paginatedQuery) as LpnPage[];

    /**
     * Compute and attach display properties for a single LPN page record.
     * Mutates tracked fields on the record in-place (same pattern as before).
     */
    const processPage = async (p: LpnPage): Promise<LpnPage> => {
      const setting = await p.settingForCompany(companySettings.id);
      const isCompanyCreated = p.isCompanyCreated === true;

      // If this page was created by *some* company but not ours, drop it later.
      const createdByOtherCompany =
        isCompanyCreated && (!setting || setting.companySetting?.id !== companySettings.id);

      const isEnabled = setting?.isEnabled ?? true;
      const employerRecommended = setting?.employerRecommended === true || isCompanyCreated;

      const badgeCappedTuition = isCompanyCreated
        ? (p.badgeCappedTuition ?? false)
        : (setting?.cappedTuition ?? p.badgeCappedTuition ?? false);

      let hasDiscount;
      if (isCompanyCreated) {
        hasDiscount = p.badgeDiscount ?? false;
      } else {
        if (setting?.hasDiscount) {
          hasDiscount = true;
        } else {
          hasDiscount = p.badgeDiscount ?? false;
        }
      }

      let discountPercent = '';
      if (hasDiscount) {
        if (isCompanyCreated) {
          discountPercent = p.discountPercent || '';
        } else {
          if (setting && setting.discountPercent > 0) {
            discountPercent = setting.discountPercent.toString();
          } else if (Number(p.discountPercent) > 0) {
            discountPercent = p.discountPercent;
          } else {
            discountPercent = '';
          }
        }
        // Always convert to string
        discountPercent = discountPercent.toString();
      }

      p.setting = setting;
      p.isCompanyCreated = isCompanyCreated;
      p.isEnabled = isEnabled;
      p.employerRecommended = employerRecommended;
      p.badgeCappedTuition = badgeCappedTuition;
      p.hasDiscount = hasDiscount;
      p.discountPercent = discountPercent;
      p.createdByOtherCompany = createdByOtherCompany;

      return p;
    };

    // company-lpn-settings are sideloaded via `include`, so settingForCompany
    // resolves from the store's identity map. Process all pages concurrently.
    const [processedRecommended, processedPaginated] = await Promise.all([
      Promise.all((recommendedRaw as LpnPage[]).map(processPage)),
      Promise.all((paginatedRaw as LpnPage[]).map(processPage)),
    ]);

    // Build the recommended section from the dedicated query — covers *all*
    // recommended pages regardless of alphabetical position in the full list.
    const recommendedIds = new Set<string | null>();

    // Client-side post-filter: the API savings filter matches on global badge
    // values only and can't account for company-level overrides. After
    // processPage has resolved p.setting and p.isCompanyCreated, compute the
    // effective badge value inline and drop tiles where it is false (DEV-6509).
    // Reading from p.setting avoids mutating @attr fields on the Ember Data
    // record, which would incorrectly mark it dirty.
    const passesActiveSavingsFilters = (p: LpnPage) => {
      if (!savings.length) return true;
      const setting = p.setting as Record<string, unknown> | undefined;
      const isCompanyCreated = p.isCompanyCreated === true;
      return (savings as SavingsTag[]).every((tag) => {
        switch (tag) {
          case 'capped_tuition':
            // Already computed as the effective company-aware value by processPage.
            return Boolean(p.badgeCappedTuition);
          case 'family_eligible': {
            const v = isCompanyCreated
              ? p.badgeFamilySavings
              : ((setting?.['badgeFamilySavings'] ?? p.badgeFamilySavings) as boolean | undefined);
            return Boolean(v);
          }
          case 'no_fees': {
            const v = isCompanyCreated
              ? p.badgeFeesWaived
              : ((setting?.['badgeFeesWaived'] ?? p.badgeFeesWaived) as boolean | undefined);
            return Boolean(v);
          }
          default:
            return true;
        }
      });
    };

    const recommendedPartners = processedRecommended
      .filter((p) => !p.createdByOtherCompany)
      .filter((p) => p.isEnabled)
      .filter(passesActiveSavingsFilters)
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName))
      .filter((p) => {
        // Deduplicate in case the same page appears twice in the raw result.
        if (recommendedIds.has(p.id)) return false;
        recommendedIds.add(p.id);
        return true;
      });

    // All-partners list: the paginated slice, with recommended pages excluded
    // so they don't appear in both sections.
    const allPartners = processedPaginated
      .filter((p) => !p.createdByOtherCompany)
      .filter((p) => p.isEnabled)
      .filter(passesActiveSavingsFilters)
      .filter((p) => !recommendedIds.has(p.id))
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName));

    return {
      recommendedPartners,
      allPartners,
      companySettings,
      totalItems: meta.page.total || 0,
    };
  }
}
